<script setup lang="ts">
import type { ChipColor } from '@/components/base/BaseChip.vue'
import type { Icons } from '@/components/base/BaseIcon.vue'

export type BulkOptinOption = {
  id: number
  text: string
  value: boolean | null
  badgeIcon: Icons
  badgeColor: ChipColor
  badgeText: string
}

defineProps<{
  label: string
  options: BulkOptinOption[]
  placeholder: string
}>()

const model = defineModel<number | null>({ default: null })

const toOptinOption = (option: Record<string, unknown>) => option as BulkOptinOption & { label: string }
</script>

<template>
  <div class="w-full">
    <label class="mb-1 block text-sm font-medium text-foreground">
      {{ label }}
    </label>
    <ComboboxSelect
      v-model="model"
      value-key="id"
      label-key="text"
      :placeholder="placeholder"
      :options="options"
      trigger-class="w-full"
      clearable
    >
      <template #trigger="{ selected, placeholder: triggerPlaceholder }">
        <div v-if="selected" class="flex min-w-0 flex-1 items-center gap-2">
          <BaseChip
            :color="toOptinOption(selected).badgeColor"
            :icon="toOptinOption(selected).badgeIcon"
            :text="toOptinOption(selected).badgeText"
          />
          <span class="truncate">{{ selected.label }}</span>
        </div>
        <span v-else class="truncate text-muted-foreground">{{ triggerPlaceholder }}</span>
      </template>
      <template #option="{ option, selected }">
        <div class="flex items-center gap-2">
          <BaseChip
            :color="toOptinOption(option).badgeColor"
            :icon="toOptinOption(option).badgeIcon"
            :text="toOptinOption(option).badgeText"
          />
          <span :class="selected ? 'font-semibold text-primary' : ''">
            {{ option.label }}
          </span>
        </div>
      </template>
    </ComboboxSelect>
  </div>
</template>
